<template>
    <div class="filter-steps--container">
        <team-region-member-filter-options
            v-if="showTeamRegionFilter && (canViewTeamRegion || isCustomerAdmin)"
            :team-region-id="internalFilters.teamRegionId"
            :team-member-public-userid="internalFilters.teamMemberPublicUserId"
            @changed="handleTeamRegionMemberChanged"
            :strict-team-region-filter="strictTeamRegionFilter"
            :clearable="clearable"
            :show-carriers="showCarriers"
            :show-team-member="showTeamMembers"
            :use-in-map="useInMap"
        ></team-region-member-filter-options>

        <div v-if="showCustomerFilter && hasCustomerFilterAccess" class="filter-item">
            <customer-filter-options
                class="filter-steps--choices filter-status"
                @onCustomerSelected="handleCustomerChanged"
                :filter-customer-id="internalFilters.customerId"
            />
        </div>

        <md-field v-if="showStatusFilter" :class="statusFilterClass">
            <vue-select
                :data-testid="statusTestId"
                v-if="statusOptions"
                :options="statusOptions"
                :placeholder="statusPlaceholder"
                v-model="internalFilters.status"
                :clearable="statusClearable"
                :searchable="statusSearchable"
            ></vue-select>
        </md-field>

        <md-field v-if="dateMode === 'single'" :class="[singleDateClass, !dateClearable ? 'hide-date-clear' : '']">
            <md-datepicker
                :data-testid="singleDateTestId"
                class="filter-steps--choices filter-step-date"
                v-model="internalFilters.singleDate"
                md-immediately
                :md-debounce="100"
                ref="mdDatePicker"
                :clearable="dateClearable"
            ></md-datepicker>
        </md-field>

        <template v-if="dateMode === 'range'">
            <md-field :class="['start-date-field', !dateClearable ? 'hide-date-clear' : '']">
                <md-datepicker
                    :data-testid="fromDateTestId"
                    class="filter-steps--choices filter-step-date start-date"
                    v-model="internalFilters.fromDate"
                    md-immediately
                    :md-debounce="100"
                    :clearable="dateClearable"
                ></md-datepicker>
            </md-field>
            <md-field :class="['end-date-field', !dateClearable ? 'hide-date-clear' : '']">
                <md-datepicker
                    :data-testid="toDateTestId"
                    class="filter-steps--choices filter-step-date end-date"
                    v-model="internalFilters.toDate"
                    md-immediately
                    :md-debounce="100"
                    :clearable="dateClearable"
                ></md-datepicker>
            </md-field>
        </template>

        <md-field class="time-picker md-theme-default" v-if="showTimeWindow && !isGeocodedView">
            <time-picker
                :key="timePickerComponentKey"
                :time="selectedTime"
                @selectedTime="getTimeWindow"
                :is-appointment-time-included="false"
                :additional-time-options="additionalTimeWindowOptions"
                :is-use-in-filter="true"
                @customTimeWindowModal="showCustomTimeWindowModal"
            />
        </md-field>

        <md-field v-if="showQuotingStatus">
            <quoting-status-filter @changed="handleQuotingStatusChanged" />
        </md-field>

        <div v-if="showBlankDateToggle" class="filter-item filter-item--checkbox">
            <md-checkbox
                type="checkbox"
                v-model="internalFilters.showBlankDateFirst"
                @change="handleBlankDateToggleChanged"
            >
                {{ blankDateLabel }}
            </md-checkbox>
        </div>

        <slot name="extra-filters" :filters="internalFilters" :update-filter="updateFilter"></slot>

        <slot name="trailing-actions"></slot>
    </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { DATE_TYPES } from '@/utils/constants';
import TeamRegionMemberFilterOptions from '@/components/TeamRegionMemberFilterOptions';
import CustomerFilterOptions from '@/components/CustomerFilterOptions';
import TimePicker from '@/components/TimePicker';
import TimePickerCustomTimeWindowModal from '@/components/TimePickerCustomTimeWindowModal';
import QuotingStatusFilter from '@/components/JobOffers/QuotingStatusFilter';

export default {
    name: 'BaseFilter',
    components: { TeamRegionMemberFilterOptions, CustomerFilterOptions, TimePicker, QuotingStatusFilter },
    props: {
        statusOptions: {
            type: Array,
            default: () => []
        },
        dateMode: {
            type: String,
            default: 'range',
            validator: (value) => ['single', 'range', 'none'].includes(value)
        },
        showStatusFilter: {
            type: Boolean,
            default: true
        },
        showTeamRegionFilter: {
            type: Boolean,
            default: true
        },
        showTeamMembers: {
            type: Boolean,
            default: true
        },
        showCarriers: {
            type: Boolean,
            default: false
        },
        showCustomerFilter: {
            type: Boolean,
            default: true
        },
        showTimeWindow: {
            type: Boolean,
            default: false
        },
        showDriverRating: {
            type: Boolean,
            default: false
        },
        showQuotingStatus: {
            type: Boolean,
            default: false
        },
        showBlankDateToggle: {
            type: Boolean,
            default: false
        },
        strictTeamRegionFilter: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        dateClearable: {
            type: Boolean,
            default: true
        },
        statusClearable: {
            type: Boolean,
            default: true
        },
        statusSearchable: {
            type: Boolean,
            default: true
        },
        useInMap: {
            type: Boolean,
            default: false
        },
        debounceMs: {
            type: Number,
            default: 700
        },
        teamRegionId: {
            type: Number,
            default: null
        },
        initialFilters: {
            type: Object,
            default: () => ({})
        },
        statusPlaceholder: {
            type: String,
            default: 'Status'
        },
        defaultStatus: {
            type: String,
            default: null
        },
        statusTestId: {
            type: String,
            default: 'status-filter'
        },
        singleDateTestId: {
            type: String,
            default: 'date-filter'
        },
        fromDateTestId: {
            type: String,
            default: 'start-date'
        },
        toDateTestId: {
            type: String,
            default: 'end-date'
        },
        statusFilterClass: {
            type: String,
            default: 'status-filter'
        },
        singleDateClass: {
            type: String,
            default: 'datepicker-container'
        },
        domain: {
            type: String,
            default: null,
            validator: (value) => !value || ['trips', 'stops', 'shipments'].includes(value)
        },
        timeWindowFilterOptions: {
            type: Array,
            default: () => []
        },
        isGeocodedView: {
            type: Boolean,
            default: false
        },
        blankDateLabel: {
            type: String,
            default: 'Show items with no date first'
        }
    },
    data() {
        return {
            internalFilters: {
                status: this.defaultStatus,
                teamMemberPublicUserId: '',
                carrierTeamId: null,
                teamRegionId: null,
                customerId: null,
                singleDate: null,
                fromDate: null,
                toDate: null,
                timeWindowStart: null,
                timeWindowEnd: null,
                driverRating: null,
                quotingStatus: null,
                showBlankDateFirst: false
            },
            additionalTimeWindowOptions: [],
            timePickerComponentKey: 0,
            selectedTime: null,
            lastValidSingleDate: null
        };
    },
    computed: {
        ...mapGetters({
            teamRegions: 'team/teamRegions',
            user: 'user/user',
            canEditOwnTeamRegion: 'user/canEditOwnTeamRegion',
            canViewTeamRegion: 'user/canViewTeamRegion',
            isSingleUser: 'user/isIndividualUser',
            isSingleTeamMember: 'team/isSingleTeamMember',
            isReadOnlyUser: 'user/isReadOnlyUser',
            hasCustomerFilterAccess: 'user/hasInventoryListAccess',
            isCustomerAdmin: 'user/isCustomerAdmin',
            hasElevatedTeamRegionFilter: 'user/hasElevatedTeamRegionFilter'
        })
    },
    watch: {
        debounceMs() {
            if (this.$handleFilterDebounced && this.$handleFilterDebounced.cancel) {
                this.$handleFilterDebounced.cancel();
            }
            this.$handleFilterDebounced = _.debounce(() => {
                this.emitFilters();
            }, this.debounceMs);
        },
        teamRegionId: {
            immediate: true,
            handler(newVal) {
                if (newVal !== undefined) {
                    this.internalFilters.teamRegionId = newVal;
                }
            }
        },
        'internalFilters.status': function(newValue) {
            if (
                this.showDriverRating &&
                newValue != null &&
                !(newValue.toLowerCase() === 'complete' || newValue.toLowerCase() === 'all')
            ) {
                this.internalFilters.driverRating = null;
            }
            this.handleFilter();
        },
        'internalFilters.singleDate': function(val) {
            if (this.dateMode === 'single') {
                // For trips, ensure date is always filled and cannot be cleared
                if (this.domain === 'trips') {
                    if (!val) {
                        // Restore the last valid date, or today if none
                        const fallback = this.lastValidSingleDate || moment().format('YYYY-MM-DD');
                        this.$nextTick(() => {
                            this.internalFilters.singleDate = fallback;
                        });
                        return;
                    }
                    // Update last valid date when a valid date is set
                    this.lastValidSingleDate = val;
                }
                this.handleFilter();
            }
        },
        'internalFilters.fromDate': function(newVal, oldVal) {
            if (this.dateMode === 'range') {
                if (
                    moment(newVal).format(DATE_TYPES.internationalDate) ===
                    moment(oldVal).format(DATE_TYPES.internationalDate)
                ) {
                    return;
                }

                if (
                    this.internalFilters.fromDate &&
                    this.internalFilters.toDate &&
                    moment(newVal).format(DATE_TYPES.internationalDate) >
                        moment(this.internalFilters.toDate).format(DATE_TYPES.internationalDate)
                ) {
                    this.$notify({
                        message: 'From date cannot be later than the to date.',
                        type: 'danger'
                    });
                    return;
                }

                this.handleFilter();
            }
        },
        'internalFilters.toDate': function(newVal, oldVal) {
            if (this.dateMode === 'range') {
                if (
                    moment(newVal).format(DATE_TYPES.internationalDate) ===
                    moment(oldVal).format(DATE_TYPES.internationalDate)
                ) {
                    return;
                }

                if (
                    this.internalFilters.fromDate &&
                    this.internalFilters.toDate &&
                    moment(newVal).format(DATE_TYPES.internationalDate) <
                        moment(this.internalFilters.fromDate).format(DATE_TYPES.internationalDate)
                ) {
                    this.$notify({
                        message: 'To date cannot be earlier than the from date.',
                        type: 'danger'
                    });
                    return;
                }

                this.handleFilter();
            }
        },
        'internalFilters.driverRating': function() {
            if (this.showDriverRating) {
                this.handleFilter();
            }
        },
        initialFilters: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal) {
                    // On first run (oldVal is undefined), apply all values including nulls
                    // On subsequent runs, only update properties that have changed and have meaningful values
                    // to avoid resetting user-selected filter values
                    if (oldVal === undefined) {
                        this.internalFilters = { ...this.internalFilters, ...newVal };
                    } else {
                        Object.keys(newVal).forEach((key) => {
                            const hasValue = newVal[key] !== null && newVal[key] !== undefined && newVal[key] !== '';
                            // Only update if the value changed and has a meaningful value
                            if (newVal[key] !== oldVal[key] && hasValue) {
                                this.internalFilters[key] = newVal[key];
                            }
                        });
                    }
                }
            }
        },
        timeWindowFilterOptions() {
            if (this.showTimeWindow && this.timeWindowFilterOptions.length > 0) {
                this.timeWindowFilterOptions.forEach((timeWindowFilter) => {
                    if (!this.additionalTimeWindowOptions.find((x) => x.name === timeWindowFilter.name))
                        this.additionalTimeWindowOptions.push(timeWindowFilter);
                });
                this.timePickerComponentKey += 1;
            }
        }
    },
    mounted() {
        if (!this.$root.isDesktop) {
            if (this.$refs.mdDatePicker) {
                this.$refs.mdDatePicker.$refs.input.$el.setAttribute('readonly', true);
            }
        }

        if (this.showTimeWindow && localStorage.getItem('stops.defaults.time-filter-options') !== null) {
            const localStorageOptions = JSON.parse(localStorage.getItem('stops.defaults.time-filter-options'));
            if (localStorageOptions.length > 0) {
                localStorageOptions.forEach((timeWindowFilter) => {
                    this.additionalTimeWindowOptions.push(timeWindowFilter);
                });
            }
        }

        // For trips with single date mode, ensure date is always filled
        if (this.domain === 'trips' && this.dateMode === 'single') {
            if (!this.internalFilters.singleDate) {
                this.internalFilters.singleDate = moment().format('YYYY-MM-DD');
            }
            this.lastValidSingleDate = this.internalFilters.singleDate;
        }
    },
    beforeDestroy() {
        if (this.$handleFilterDebounced && this.$handleFilterDebounced.cancel) {
            this.$handleFilterDebounced.cancel();
        }
    },
    methods: {
        handleFilter() {
            if (!this.$handleFilterDebounced) {
                this.$handleFilterDebounced = _.debounce(() => {
                    this.emitFilters();
                }, this.debounceMs);
            }
            this.$handleFilterDebounced();
        },
        emitFilters() {
            this.$nextTick(() => {
                this.$emit('update:filters', { ...this.internalFilters });

                if (this.domain) {
                    this.emitDomainSpecificEvent();
                }
            });
        },
        emitDomainSpecificEvent() {
            if (this.domain === 'trips') {
                let tripDate = '';
                if (this.internalFilters.singleDate) {
                    tripDate = moment(this.internalFilters.singleDate).format('YYYY-MM-DD');
                }

                this.$emit('onFilterTrips', {
                    tripDate,
                    teamRegionId: this.internalFilters.teamRegionId,
                    elevatedTeamRegionFilter: this.hasElevatedTeamRegionFilter,
                    statusFilter: this.internalFilters.status,
                    teamMemberPublicUserId: this.internalFilters.teamMemberPublicUserId,
                    referenceNumber: '',
                    customerId: this.internalFilters.customerId
                });
            } else if (this.domain === 'stops') {
                let fromDate = null;
                if (this.internalFilters.fromDate) {
                    fromDate = moment(this.internalFilters.fromDate).format('YYYY-MM-DD');
                }

                let toDate = null;
                if (this.internalFilters.toDate) {
                    toDate = moment(this.internalFilters.toDate).format('YYYY-MM-DD');
                }

                this.$emit('onFilterStops', {
                    status: this.internalFilters.status,
                    teamMemberPublicUserId: this.internalFilters.teamMemberPublicUserId,
                    carrierTeamId: this.internalFilters.carrierTeamId,
                    timeWindowStart: this.internalFilters.timeWindowStart,
                    timeWindowEnd: this.internalFilters.timeWindowEnd,
                    teamRegionId: this.internalFilters.teamRegionId,
                    customerId: this.internalFilters.customerId,
                    fromDate,
                    toDate,
                    showBlankStopDateFirst: this.internalFilters.showBlankDateFirst,
                    driverRating: this.internalFilters.driverRating
                });
            } else if (this.domain === 'shipments') {
                let fromDate = '';
                if (this.internalFilters.fromDate) {
                    fromDate = moment(this.internalFilters.fromDate).format('YYYY-MM-DD');
                }

                let toDate = '';
                if (this.internalFilters.toDate) {
                    toDate = moment(this.internalFilters.toDate).format('YYYY-MM-DD');
                }

                this.$emit('onFilterShipments', {
                    status: this.internalFilters.status,
                    teamMemberPublicUserId: this.internalFilters.teamMemberPublicUserId,
                    carrierTeamId: this.internalFilters.carrierTeamId,
                    fromDate,
                    toDate,
                    teamRegionId: this.internalFilters.teamRegionId,
                    quotingStatus: this.internalFilters.quotingStatus,
                    customerId: this.internalFilters.customerId,
                    showBlankShipmentDateFirst: this.internalFilters.showBlankDateFirst
                });
            }
        },
        handleTeamRegionMemberChanged(val) {
            this.internalFilters.teamMemberPublicUserId = val.type === 'Member' ? val.publicUserId : null;
            this.internalFilters.carrierTeamId = val.type === 'Carrier' ? val.carrierTeamId : null;
            this.internalFilters.teamRegionId = val.teamRegionId;
            this.handleFilter();
        },
        handleCustomerChanged(value) {
            this.internalFilters.customerId = value;
            this.handleFilter();
        },
        getTimeWindow(selectedTime, isTimeValid) {
            if (selectedTime != null) {
                const { timeWindowStart, timeWindowEnd, appointmentTime } = selectedTime;

                let starTime = timeWindowStart;
                let endTime = timeWindowEnd;

                if (timeWindowStart === '00:00' && timeWindowEnd === '23:59') {
                    starTime = null;
                    endTime = null;
                }

                if (appointmentTime != null) {
                    starTime = appointmentTime;
                    endTime = appointmentTime;
                }

                this.internalFilters.timeWindowStart = starTime;
                this.internalFilters.timeWindowEnd = endTime;
            } else {
                this.internalFilters.timeWindowStart = null;
                this.internalFilters.timeWindowEnd = null;
            }
            if (isTimeValid) {
                this.handleFilter();
            }
        },
        showCustomTimeWindowModal(oldTime) {
            let time = oldTime;
            this.$modal.show(TimePickerCustomTimeWindowModal).then((response) => {
                if (response) {
                    const index = this.timeWindowFilterOptions.findIndex((x) => x.name === response.name);

                    if (index === -1) {
                        this.timeWindowFilterOptions.push(response);

                        let localStorageOptions = [];
                        if (localStorage.getItem('stops.defaults.time-filter-options') !== null) {
                            localStorageOptions = JSON.parse(
                                localStorage.getItem('stops.defaults.time-filter-options')
                            );
                            if (localStorageOptions.length === 5) {
                                localStorageOptions.shift();
                            }
                        }
                        localStorageOptions.push(response);
                        localStorage.setItem('stops.defaults.time-filter-options', JSON.stringify(localStorageOptions));
                        this.$modal.hide();
                    }
                    time = response.name;
                }

                this.selectedTime = time;
                this.timePickerComponentKey += 1;
            });
        },
        handleQuotingStatusChanged(val) {
            this.internalFilters.quotingStatus = val;
            this.handleFilter();
        },
        handleBlankDateToggleChanged() {
            // Emit immediately (bypass debounced handleFilter)
            this.$nextTick(() => {
                this.$emit('update:filters', { ...this.internalFilters });
                if (this.domain) {
                    this.emitDomainSpecificEvent();
                }
            });
        },
        updateFilter(key, value) {
            this.internalFilters[key] = value;
            this.handleFilter();
        },
        clearFilters() {
            // For trips with single date, preserve the date (cannot be cleared)
            const preservedSingleDate =
                this.domain === 'trips' && this.dateMode === 'single'
                    ? this.internalFilters.singleDate || moment().format('YYYY-MM-DD')
                    : null;

            this.internalFilters = {
                status: this.defaultStatus,
                teamMemberPublicUserId: '',
                carrierTeamId: null,
                teamRegionId: null,
                customerId: null,
                singleDate: preservedSingleDate,
                fromDate: null,
                toDate: null,
                timeWindowStart: null,
                timeWindowEnd: null,
                driverRating: null,
                quotingStatus: null,
                showBlankDateFirst: false
            };
            this.handleFilter();
        }
    }
};
</script>

<style lang="scss" scoped>
.filter-steps--container {
    ::v-deep .md-field {
        display: inline-block;
        width: auto;
        min-width: 200px;
        min-height: 48px;
        margin-right: 20px;
        margin-top: 0;
        vertical-align: top;
    }

    .filter-item {
        display: inline-block;
        width: auto;
        min-width: 200px;
        min-height: 48px;
        margin-right: 20px;
        vertical-align: top;
    }

    .filter-step-date {
        min-width: 250px;
        margin-right: 0;
        min-height: initial;
    }

    .status-filter {
        min-width: 150px;
        margin-right: 0;
        min-height: initial;
    }

    .filter-button--container {
        display: inline-block;
    }

    ::v-deep .md-datepicker:after,
    ::v-deep .md-datepicker:before {
        bottom: 0;
        height: 0;
    }

    ::v-deep .md-datepicker:before,
    ::v-deep .md-datepicker:after {
        bottom: 30px;
        color: #aaaaaa;
        font-weight: 400;
        font-size: 14px;
        transform: none;
        z-index: 1;
    }

    .start-date:before,
    .start-date:after {
        content: 'Start Date';
    }

    .end-date:before,
    .end-date:after {
        content: 'End Date';
    }

    ::v-deep .md-has-value:before,
    ::v-deep .md-has-value:after {
        bottom: 0;
        height: 0;
        content: '';
    }

    ::v-deep .md-datepicker .md-input {
        position: absolute;
        z-index: 9;
        width: 150px;
    }

    ::v-deep .md-datepicker .md-input-action {
        z-index: 99;
        right: 52px; // default when clear icon is present
    }
}

/* Hide clear button when dateClearable is false and reclaim space */
.hide-date-clear {
    ::v-deep .md-clear {
        display: none !important;
    }
    /* Pull action buttons (calendar icon) back to the right edge now that clear icon is removed */
    ::v-deep .md-datepicker .md-input-action {
        right: 28px; // align closer; tweak if needed
    }
}

::v-deep .vs__search::placeholder {
    color: #aaaaaa;
    font-size: 14px;
}

::v-deep .vs--searchable .vs__dropdown-toggle {
    border: none;
    padding-bottom: 5px;
    margin-top: 4px;
    background-color: transparent;
    cursor: pointer;
}

::v-deep .vs--disabled .vs__clear,
::v-deep .vs--disabled .vs__dropdown-toggle,
::v-deep .vs--disabled .vs__open-indicator,
::v-deep .vs--disabled .vs__search,
::v-deep .vs--disabled .vs__selected {
    background-color: transparent;
}

::v-deep .vs__search {
    cursor: pointer;
}

.datepicker-noevent {
    ::v-deep .md-datepicker {
        input {
            pointer-events: none;
        }
    }
}

.time-picker .main-time-picker-container:after {
    content: 'Select time';
    color: #aaaaaa;
    font-weight: 400;
    font-size: 14px;
    transform: none;
    bottom: 17px;
    position: absolute;
}

::v-deep .md-has-value .main-time-picker-container:after {
    content: '';
}

.time-picker {
    width: 210px !important;
}

.time-picker.md-field:after {
    bottom: 10px !important;
    height: 1px !important;
}

.time-picker:last-child {
    margin-right: 0 !important;
}

.time-picker ::v-deep .time-picker-container:nth-child(2) {
    border-bottom: 1px solid #d2d2d2;
}
</style>
